import React from 'react';
import { connect, DispatchProp } from 'react-redux';

import { Collapsible, MarketplaceCta, HtmlContent, DisabledLockSvg } from '@dndbeyond/character-components/es';
import {
    rulesEngineSelectors,
    AccessUtils,
    CharacterUtils,
    ConditionContract,
    Constants,
    FormatUtils,
    serviceDataActions,
    Vehicle,
    VehicleComponentUtils,
    VehicleUtils,
    RuleData,
    RuleDataUtils,
    CharacterTheme,
} from '@dndbeyond/character-rules-engine/es';

import { sidebarActions } from '../../../actions';
import CustomizeDataEditor from '../../../components/CustomizeDataEditor';
import EditorBox from '../../../components/EditorBox';
import PaneInitFailureContent from '../../../components/Pane/PaneInitFailureContent';
import { SidebarHeader, SidebarSubheading } from '../../../components/Sidebar';
import VehicleBlock from '../../../components/VehicleBlock';
import VehicleConditionTrackers from '../../../components/VehicleConditionsTracker';
import VehicleFuelTracker from '../../../components/VehicleFuelTracker';
import VehicleHealthAdjuster from '../../../components/VehicleHealthAdjuster';
import { RemoveButton } from '../../../components/common/Button';
import { appEnvSelectors } from '../../../selectors';
import { SharedAppState } from '../../../stores/typings';
import { PaneComponentEnum, ComponentUtils, PaneIdentifierUtils, TypeScriptUtils } from '../../../utils';
import { PaneIdentifiersVehicle } from '../../../utils/PaneIdentifier/typings';

interface Hack_DataForEditor {
    name: string | null;
    notes: string | null;
}
interface Hack_DataForAction {
    name: string | null;
    description: string | null;
}

interface Props extends DispatchProp {
    vehicles: Array<Vehicle>;
    identifiers: PaneIdentifiersVehicle | null;
    isReadonly: boolean;
    ruleData: RuleData;
    theme: CharacterTheme;
}

interface State {
    vehicle: Vehicle | null;
}
class VehiclePane extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = this.generateStateData(props);
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>): void {
        const { vehicles, identifiers } = this.props;

        if (vehicles !== prevProps.vehicles || identifiers !== prevProps.identifiers) {
            this.setState(this.generateStateData(this.props));
        }
    }

    generateStateData = (props: Props): State => {
        const { vehicles, identifiers } = props;

        let vehicle: Vehicle | null | undefined = null;
        if (identifiers !== null) {
            vehicle = vehicles.find((vehicle) => identifiers.id === VehicleUtils.getMappingId(vehicle));
        }

        return {
            vehicle: vehicle ? vehicle : null,
        };
    };

    handleComponentClick = (componentId: number, vehicleId: number): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryPush(
                PaneComponentEnum.VEHICLE_COMPONENT,
                PaneIdentifierUtils.generateVehicleComponent(componentId, vehicleId),
            ),
        );
    };

    handleActionStationClick = (stationId: number, vehicleId: number): void => {
        const { dispatch } = this.props;

        dispatch(
            sidebarActions.paneHistoryPush(
                PaneComponentEnum.VEHICLE_ACTION_STATION,
                PaneIdentifierUtils.generateVehicleActionStation(stationId, vehicleId),
            ),
        );
    };

    handleRemove = (): void => {
        const { vehicle } = this.state;
        const { dispatch } = this.props;

        if (vehicle === null) {
            return;
        }

        dispatch(sidebarActions.paneHistoryStart(PaneComponentEnum.EXTRA_MANAGE));
        dispatch(serviceDataActions.vehicleMappingRemove(VehicleUtils.getMappingId(vehicle)));
    };

    hack__transformDataForEditor = (data: Hack_DataForAction): Hack_DataForEditor => {
        return {
            name: data.name,
            notes: data.description,
        };
    };

    hack__transformDataForAction = (data: Hack_DataForEditor): Hack_DataForAction => {
        return {
            name: data.name,
            description: data.notes,
        };
    };

    handleDataUpdate = (properties: Hack_DataForEditor): void => {
        const { vehicle } = this.state;
        const { dispatch } = this.props;

        if (!vehicle) {
            return;
        }

        const data = this.getData();
        const prevData: Partial<Hack_DataForEditor> = data ? data : {};

        const newProperties: Hack_DataForEditor = {
            ...prevData,
            ...properties,
        };

        dispatch(
            serviceDataActions.vehicleMappingDataSet(
                VehicleUtils.getMappingId(vehicle),
                this.hack__transformDataForAction(newProperties),
            ),
        );
    };

    handleHealthAdjusterSave = (hitPointDiff: number): void => {
        const { vehicle } = this.state;
        const { dispatch } = this.props;

        if (!vehicle) {
            return;
        }

        const primaryComponent = VehicleUtils.getPrimaryComponent(vehicle);

        let id = VehicleComponentUtils.getMappingId(primaryComponent);
        let hitPointInfo = VehicleComponentUtils.getHitPointInfo(primaryComponent);

        if (hitPointInfo === null) {
            return;
        }

        let newHitPoints = CharacterUtils.calculateHitPoints(hitPointInfo, hitPointDiff);

        dispatch(serviceDataActions.vehicleComponentMappingHitPointsSet(id, newHitPoints.newRemovedHp));
    };

    handleFuelChange = (remainingFuel: number): void => {
        const { vehicle } = this.state;
        const { dispatch } = this.props;

        if (!vehicle) {
            return;
        }

        dispatch(serviceDataActions.vehicleMappingRemainingFuelSet(VehicleUtils.getMappingId(vehicle), remainingFuel));
    };

    handleConditionLevelChange = (conditionId: number, newLevel: number | null, prevLevel: number | null): void => {
        const { vehicle } = this.state;
        const { dispatch } = this.props;

        if (vehicle === null) {
            return;
        }

        const vehicleId = VehicleUtils.getMappingId(vehicle);

        if (!prevLevel && newLevel !== null) {
            dispatch(serviceDataActions.vehicleMappingConditionAdd(vehicleId, conditionId, newLevel));
        } else if (!newLevel) {
            dispatch(serviceDataActions.vehicleMappingConditionRemove(vehicleId, conditionId));
        } else {
            dispatch(
                serviceDataActions.vehicleMappingConditionSet(
                    VehicleUtils.getMappingId(vehicle),
                    conditionId,
                    newLevel,
                ),
            );
        }
    };

    getData = (): Hack_DataForEditor => {
        const { vehicle } = this.state;

        if (!vehicle) {
            return {
                name: null,
                notes: null,
            };
        }

        const name = VehicleUtils.getName(vehicle);
        const description = VehicleUtils.getDescription(vehicle);

        return this.hack__transformDataForEditor({ name, description });
    };

    renderCustomize = (): React.ReactNode => {
        const { isReadonly } = this.props;

        if (isReadonly) {
            return null;
        }

        return (
            <div className="ct-vehicle-pane__customize">
                <Collapsible layoutType={'minimal'} header="Customize">
                    <EditorBox>
                        <CustomizeDataEditor
                            data={this.getData()}
                            enableNotes={true}
                            enableName={true}
                            onDataUpdate={this.handleDataUpdate}
                        />
                    </EditorBox>
                </Collapsible>
            </div>
        );
    };

    renderImage = (): React.ReactNode => {
        const { vehicle } = this.state;

        if (!vehicle) {
            return null;
        }

        let largeAvatarUrl = VehicleUtils.getLargeAvatarUrl(vehicle);
        if (!largeAvatarUrl) {
            return null;
        }

        const name = VehicleUtils.getDefinitionName(vehicle);
        let altText: string = name ? name : 'vehicle image';

        return (
            <div className="ct-vehicle-pane__full-image">
                <img className="ct-vehicle-pane__full-image-img" src={largeAvatarUrl} alt={altText} />
            </div>
        );
    };

    renderTags = (): React.ReactNode => {
        const { vehicle } = this.state;

        if (!vehicle) {
            return null;
        }

        const tags = VehicleUtils.getMovementNames(vehicle);

        if (!tags.length) {
            return null;
        }

        return (
            <React.Fragment>
                {tags.length && (
                    <div className="ct-vehicle-pane__tags">
                        <div className="ct-vehicle-pane__tags-label">Types:</div>
                        <div className="ct-vehicle-pane__tags-list">
                            {tags.map((tag) => (
                                <div className="ct-vehicle-pane__tag" key={tag}>
                                    {tag}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </React.Fragment>
        );
    };

    renderDescription = (): React.ReactNode => {
        const { vehicle } = this.state;

        if (!vehicle) {
            return null;
        }

        const description = VehicleUtils.getDefinitionDescription(vehicle);
        if (!description) {
            return null;
        }

        const label = VehicleUtils.getDefinitionName(vehicle);

        return (
            <div className="ct-vehicle-pane__description">
                {label && (
                    <div className="ct-vehicle-pane__description-heading">
                        <SidebarSubheading>{label}</SidebarSubheading>
                    </div>
                )}
                <HtmlContent className="ct-vehicle-pane__description-content" html={description} />
            </div>
        );
    };

    renderActions = (): React.ReactNode => {
        const { isReadonly } = this.props;

        if (isReadonly) {
            return null;
        }

        return (
            <div className="ct-vehicle-pane__actions">
                <div className="ct-vehicle-pane__action">
                    <RemoveButton onClick={this.handleRemove} />
                </div>
            </div>
        );
    };

    renderMarketplaceCta = (): React.ReactNode => {
        const { vehicle } = this.state;
        const { ruleData } = this.props;

        if (vehicle === null) {
            return null; //TODO could render a default empty state
        }

        let sourceNames: Array<string> = VehicleUtils.getSources(vehicle)
            .map((sourceMapping) => RuleDataUtils.getSourceDataInfo(sourceMapping.sourceId, ruleData))
            .filter(TypeScriptUtils.isNotNullOrUndefined)
            .map((sourceDataInfo) => sourceDataInfo.description)
            .filter(TypeScriptUtils.isNotNullOrUndefined);

        const sourceName: string | null =
            sourceNames.length > 0 ? FormatUtils.renderNonOxfordCommaList(sourceNames) : null;
        const description: string = 'To unlock this vehicle, check out the Marketplace to view purchase options.';

        return <MarketplaceCta sourceName={sourceName} description={description} />;
    };

    renderHealthAdjuster = (): React.ReactNode => {
        const { vehicle } = this.state;
        const { isReadonly } = this.props;

        if (vehicle === null) {
            return null;
        }

        const primaryComponent = VehicleUtils.getPrimaryComponent(vehicle);
        if (primaryComponent === null) {
            return null;
        }

        const hitPointInfo = VehicleComponentUtils.getHitPointInfo(primaryComponent);
        if (hitPointInfo === null) {
            return null;
        }

        return (
            <VehicleHealthAdjuster
                hitPointInfo={hitPointInfo}
                onSave={this.handleHealthAdjusterSave}
                isInteractive={!isReadonly}
            />
        );
    };

    renderFuelTracker = (): React.ReactNode => {
        const { vehicle } = this.state;
        const { isReadonly } = this.props;

        if (vehicle === null) {
            return null;
        }

        const fuelData = VehicleUtils.getFuelData(vehicle);

        if (fuelData === null) {
            return null;
        }

        return <VehicleFuelTracker fuelData={fuelData} onChange={this.handleFuelChange} isInteractive={!isReadonly} />;
    };

    renderConditionTrackers = (): React.ReactNode => {
        const { vehicle } = this.state;
        const { isReadonly, ruleData } = this.props;

        if (vehicle === null) {
            return null;
        }

        const enabledConditions: Array<ConditionContract> = VehicleUtils.getEnabledConditions(vehicle)
            .map((id) => RuleDataUtils.getCondition(id, ruleData))
            .filter(TypeScriptUtils.isNotNullOrUndefined);

        return (
            <VehicleConditionTrackers
                activeConditionLookup={VehicleUtils.getActiveConditionLookup(vehicle)}
                enabledConditions={enabledConditions}
                onLevelChange={this.handleConditionLevelChange}
                isInteractive={!isReadonly}
            />
        );
    };

    renderVehicleContent = (): React.ReactNode => {
        const { vehicle } = this.state;
        const { isReadonly, ruleData, theme } = this.props;

        if (!vehicle) {
            return null; //TODO could render a default empty state
        }

        const primaryComponentManageType = VehicleUtils.getConfigurationValue(
            Constants.VehicleConfigurationKeyEnum.PRIMARY_COMPONENT_MANAGE_TYPE,
            vehicle,
        );
        const enableConditionTracking = VehicleUtils.getConfigurationValue(
            Constants.VehicleConfigurationKeyEnum.ENABLE_CONDITIONS_TRACKING,
            vehicle,
        );
        const enableFuelTracking = VehicleUtils.getConfigurationValue(
            Constants.VehicleConfigurationKeyEnum.ENABLE_FUEL_TRACKING,
            vehicle,
        );

        return (
            <React.Fragment>
                {this.renderCustomize()}
                {primaryComponentManageType === Constants.VehicleConfigurationPrimaryComponentManageTypeEnum.VEHICLE &&
                    this.renderHealthAdjuster()}
                {enableFuelTracking && this.renderFuelTracker()}
                {enableConditionTracking && this.renderConditionTrackers()}
                <div className="ct-vehicle-pane__block">
                    <VehicleBlock
                        {...ComponentUtils.generateVehicleBlockProps(vehicle, ruleData)}
                        shouldCoalesce={false}
                        onComponentClick={this.handleComponentClick}
                        onActionStationClick={this.handleActionStationClick}
                        isInteractive={!isReadonly}
                        theme={theme}
                    />
                </div>
                {this.renderImage()}
                {this.renderTags()}
                {this.renderDescription()}
                {this.renderActions()}
            </React.Fragment>
        );
    };

    renderVehicleName = (): React.ReactNode => {
        const { vehicle } = this.state;

        if (!vehicle) {
            return null; //TODO could render a default empty state
        }

        const isAccessible = AccessUtils.isAccessible(VehicleUtils.getAccessType(vehicle));
        let name = VehicleUtils.getName(vehicle);

        return (
            <React.Fragment>
                {!isAccessible && <DisabledLockSvg className="ct-vehicle-pane__header-lock" />}
                {name}
            </React.Fragment>
        );
    };

    render() {
        const { vehicle } = this.state;

        if (!vehicle) {
            return <PaneInitFailureContent />;
        }

        const isAccessible = AccessUtils.isAccessible(VehicleUtils.getAccessType(vehicle));

        let avatarUrl = VehicleUtils.getAvatarUrl(vehicle);
        if (!avatarUrl) {
            avatarUrl = '';
        }

        return (
            <div className="ct-vehicle-pane" key={VehicleUtils.getUniqueKey(vehicle)}>
                <SidebarHeader parent="Vehicle" previewImage={avatarUrl}>
                    {this.renderVehicleName()}
                </SidebarHeader>
                {isAccessible ? this.renderVehicleContent() : this.renderMarketplaceCta()}
            </div>
        );
    }
}

function mapStateToProps(state: SharedAppState) {
    return {
        vehicles: rulesEngineSelectors.getVehicles(state),
        isReadonly: appEnvSelectors.getIsReadonly(state),
        ruleData: rulesEngineSelectors.getRuleData(state),
        theme: rulesEngineSelectors.getCharacterTheme(state),
    };
}

export default connect(mapStateToProps)(VehiclePane);
